import { AutocompleteProps as MuiAutocompleteProps } from '@mui/material';
import { AutocompleteRenderValueGetItemProps } from '@mui/material/Autocomplete';
import LangEnum from '../../../../../lang/LangEnum';
import { useTranslation } from '../../../../../modules/translation/TranslationContext';
import { UseAutocompleteRenderPropsParams } from '../autocomplete.types';
import AutocompleteChips from '../components/AutocompleteChips';
import AutocompleteOption from '../components/AutocompleteOption';
import AutocompleteTextField from '../components/AutocompleteTextField';

const useAutocompleteRenderProps = <
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>({
  multiple,
  loading,
  size,
  fullWidth,
  getOptionLabel,
  renderOption,
  getChipConfig,
  showAllChips,
  noOptionsText,
  loadingText,
  textFieldProps,
  inputRef,
  testId,
}: UseAutocompleteRenderPropsParams<T, Multiple, DisableClearable, FreeSolo>) => {
  type MuiProps = MuiAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>;

  const { translate } = useTranslation();

  const getLabel = (option: T): string =>
    getOptionLabel ? getOptionLabel(option) : String(option);

  const renderMultipleValue = (
    selected: T[],
    getItemProps: AutocompleteRenderValueGetItemProps<true>,
  ) => (
    <AutocompleteChips
      value={selected}
      getItemProps={getItemProps}
      getOptionLabel={getLabel}
      getChipConfig={getChipConfig}
      showAllChips={showAllChips}
    />
  );

  const renderValue = multiple
    ? (renderMultipleValue as unknown as MuiProps['renderValue'])
    : undefined;

  const renderDefaultOption: MuiProps['renderOption'] = (props, option, state) => (
    <AutocompleteOption
      props={props}
      option={option}
      state={state}
      getOptionLabel={getLabel}
      multiple={multiple}
    />
  );

  const renderInput = (params: Parameters<NonNullable<MuiProps['renderInput']>>[0]) => (
    <AutocompleteTextField
      params={params}
      loading={loading}
      size={size}
      fullWidth={fullWidth}
      inputRef={inputRef}
      testId={testId}
      {...textFieldProps}
    />
  );

  return {
    renderInput,
    renderOption: renderOption ?? renderDefaultOption,
    renderValue,
    noOptionsText: noOptionsText ?? translate(LangEnum.GLOBAL_NO_OPTIONS),
    loadingText: loadingText ?? translate(LangEnum.GLOBAL_SEARCHING),
  };
};

export default useAutocompleteRenderProps;
